// src/utils/classMinutes.ts
//
// Single source of truth for "how many minutes does this class contribute to a
// teacher's load?".
//
// Six call sites each carried their own copy of this fallback chain and every
// one of them omitted `minutesPerWeek` — the field the grid importer actually
// populates. `classMinutes` is a per-class override that almost nothing sets
// (2 of 992 classes in the 2027 Main Grid), so the chain fell through to
// `grid.defaultClassMinutes`, which transformGrid defaults to a hardcoded 70
// when the grid row has no stored `grid_data.default_class_minutes`. The 2027
// grids were written without that key, so every 2027 class was costed at 70
// minutes instead of its real 440 — understating every teaching load ~6x.
// See issue #220.
//
// Order of precedence, most specific first:
//   1. classMinutes              — explicit per-class override
//   2. minutesPerWeek            — the class's own minutes, as imported
//   3. sourceGridDefaultMinutes  — originating grid's default, for merged grids
//   4. grid.defaultClassMinutes  — this grid's default
//   5. fallback                  — caller's last resort

import { DEFAULT_MINUTES_PER_LINE } from '../types/schoolConfig';

interface MinutesBearingClass {
  classMinutes?: number;
  minutesPerWeek?: number;
  sourceGridDefaultMinutes?: number;
}

interface MinutesBearingGrid {
  defaultClassMinutes?: number;
}

export function resolveClassMinutes(
  classItem: MinutesBearingClass | null | undefined,
  grid?: MinutesBearingGrid | null,
  fallback: number = DEFAULT_MINUTES_PER_LINE
): number {
  return (
    classItem?.classMinutes ||
    classItem?.minutesPerWeek ||
    classItem?.sourceGridDefaultMinutes ||
    grid?.defaultClassMinutes ||
    fallback
  );
}

export default resolveClassMinutes;
